import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import {
  fetchParkDashboard,
  fetchAttendanceTrend,
  fetchDailyAttendanceOverview,
  fetchUserDistributionByDistrict,
} from "../redux/actions/dashboardParkActions";

import {
  FiUsers,
  FiMapPin,
  FiActivity,
  FiLogIn,
  FiLogOut,
  FiTrendingUp,
  FiDownload,
  FiFilter,
  FiCalendar,
  FiArrowUp,
} from "react-icons/fi";
import { TbChartArcs } from "react-icons/tb";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
} from "recharts";
import { CSVLink } from "react-csv";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#22c55e", "#06b6d4", "#8b5cf6", "#ec4899", "#14b8a6"];

// Enhanced Stat Card with gradient and animation
const StatCard = ({ icon: Icon, label, value, gradient = "from-blue-500 to-blue-600", isLoading, change }) => {
  if (isLoading) return <Skeleton height={160} className="rounded-3xl" />;

  return (
    <div className="group relative overflow-hidden bg-white rounded-3xl p-6 shadow-lg border border-gray-200 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500">
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

      <div className="relative z-10 flex items-start justify-between">
        <div className="flex-1">
          <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-3">{label}</p>
          <div className="flex items-end gap-2">
            <p className="text-5xl font-black bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              {value}
            </p>
            {change && (
              <div className="flex items-center gap-1 text-green-600 mb-1 text-sm font-semibold">
                <FiArrowUp size={16} />
                {change}%
              </div>
            )}
          </div>
        </div>
        <div className={`p-4 rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg group-hover:scale-125 transition-transform duration-500 group-hover:rotate-12`}>
          <Icon size={28} />
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r ${gradient} rounded-b-3xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
    </div>
  );
};

// Custom Tooltip for Charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-2xl shadow-2xl border border-gray-200">
        <p className="font-bold text-gray-900 mb-2 text-sm">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm font-semibold" style={{ color: entry.color }}>
            {entry.name}: <span>{typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const DashboardPark = () => {
  const dispatch = useDispatch();
  const { 
    parkDashboard = {}, 
    attendanceTrend = [], 
    dailyAttendanceOverview = [], 
    userDistributionByDistrict = [], 
    loading 
  } = useSelector((state) => state.dashboardPark || {});

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDistrict, setSelectedDistrict] = useState("");

  useEffect(() => {
    dispatch(fetchParkDashboard());
    dispatch(fetchAttendanceTrend());
    dispatch(fetchUserDistributionByDistrict());
  }, [dispatch]);

  useEffect(() => {
    const dateStr = moment(selectedDate).format("YYYY-MM-DD");
    dispatch(fetchDailyAttendanceOverview(dateStr));
  }, [dispatch, selectedDate]);

  const districts = [...new Set(dailyAttendanceOverview.map(d => d.district_name))].filter(Boolean);
  const filteredData = selectedDistrict
    ? dailyAttendanceOverview.filter(d => d.district_name === selectedDistrict)
    : dailyAttendanceOverview;

  const municipalityData = filteredData.map(item => ({
    municipality: item.municipality,
    total_users: item.total_users,
    present: item.present,
    absent: item.absent,
  }));

  const sorted = [...userDistributionByDistrict].sort((a, b) => b.total_users - a.total_users);
  const top10 = sorted.slice(0, 10);
  const others = sorted.slice(10).reduce((sum, d) => sum + d.total_users, 0);
  const pieData = [...top10.map(d => ({ name: d.district, value: d.total_users })), ...(others > 0 ? [{ name: "Others", value: others }] : [])];

  const csvData = filteredData.map(item => ({
    District: item.district_name,
    Municipality: item.municipality,
    "Total Users": item.total_users,
    Present: item.present,
    Absent: item.absent,
    Checkins: item.checkins,
    Checkouts: item.checkouts,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Animated background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-400 opacity-5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-40 right-20 w-80 h-80 bg-purple-400 opacity-5 rounded-full blur-3xl animate-pulse delay-700"></div>
      </div>

      {/* Header Section */}
      <div className="relative bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Live Dashboard</span>
              </div>
              <h1 className="text-5xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                Dashboard Analytics
              </h1>
              <p className="text-gray-600 text-sm">Real-time attendance monitoring & insights</p>
            </div>
            <div className="hidden lg:block text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Last Updated</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{moment().format("HH:mm:ss")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        {/* Stat Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          <StatCard
            icon={FiUsers}
            label="Total Users"
            value={loading ? "-" : (parkDashboard.total_users ?? 0).toLocaleString()}
            gradient="from-blue-500 to-blue-600"
            isLoading={loading}
          />
          <StatCard
            icon={FiMapPin}
            label="Active Parks"
            value={loading ? "-" : parkDashboard.total_parks ?? 0}
            gradient="from-emerald-500 to-green-600"
            isLoading={loading}
          />
          <StatCard
            icon={FiActivity}
            label="Today's Attendance"
            value={loading ? "-" : (parkDashboard.total_attendance ?? 0).toLocaleString()}
            gradient="from-purple-500 to-pink-600"
            isLoading={loading}
          />
          <StatCard
            icon={FiLogIn}
            label="Check-ins"
            value={loading ? "-" : (parkDashboard.today_checkins ?? 0).toLocaleString()}
            gradient="from-indigo-500 to-blue-600"
            isLoading={loading}
          />
          <StatCard
            icon={FiLogOut}
            label="Check-outs"
            value={loading ? "-" : (parkDashboard.today_checkouts ?? 0).toLocaleString()}
            gradient="from-orange-500 to-red-600"
            isLoading={loading}
          />
          <StatCard
            icon={FiTrendingUp}
            label="Attendance Rate"
            value={loading ? "-" : parkDashboard.attendance_rate ?? "0%"}
            gradient="from-teal-500 to-cyan-600"
            isLoading={loading}
          />
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {/* Attendance Trend */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all duration-500 group">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl group-hover:scale-110 transition-transform duration-500">
                <FiTrendingUp className="text-white text-xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Attendance Trend</h2>
                <p className="text-xs text-gray-500 mt-1">Last 12 months</p>
              </div>
            </div>
            {attendanceTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={attendanceTrend}>
                  <defs>
                    <linearGradient id="colorCheckin" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorCheckout" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: "20px" }} />
                  <Area type="monotone" dataKey="checkin" stroke="#3b82f6" fillOpacity={1} fill="url(#colorCheckin)" name="Check-ins" strokeWidth={3} />
                  <Area type="monotone" dataKey="checkout" stroke="#10b981" fillOpacity={1} fill="url(#colorCheckout)" name="Check-outs" strokeWidth={3} />
                  <Area type="monotone" dataKey="present" stroke="#22c55e" fillOpacity={1} fill="url(#colorPresent)" name="Present" strokeWidth={3} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-80 flex flex-col items-center justify-center text-gray-400">
                <FiTrendingUp className="text-6xl mb-3 opacity-20" />
                <p className="text-sm font-medium">No trend data available</p>
              </div>
            )}
          </div>

          {/* District Distribution */}
          <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all duration-500 group">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl group-hover:scale-110 transition-transform duration-500">
                <TbChartArcs className="text-white text-xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">District Breakdown</h2>
                <p className="text-xs text-gray-500 mt-1">Top 10 districts</p>
              </div>
            </div>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-80 flex flex-col items-center justify-center text-gray-400">
                <TbChartArcs className="text-6xl mb-3 opacity-20" />
                <p className="text-sm font-medium">No distribution data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Daily Attendance */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-200 p-8 hover:shadow-xl transition-all duration-500">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Daily Attendance</h2>
              <p className="text-sm text-gray-600 flex items-center gap-2">
                <FiCalendar className="text-orange-500" />
                {moment(selectedDate).format("dddd, MMMM D, YYYY")}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="relative">
                <FiFilter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                <select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                  className="pl-11 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 text-sm font-medium hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="">All Districts</option>
                  {districts.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div className="relative">
                <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
                <DatePicker
                  selected={selectedDate}
                  onChange={setSelectedDate}
                  className="pl-11 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 text-sm font-medium hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  dateFormat="MMM d, yyyy"
                />
              </div>

              {csvData.length > 0 && (
                <CSVLink
                  data={csvData}
                  filename={`attendance_${moment(selectedDate).format("YYYY-MM-DD")}.csv`}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl flex items-center gap-2 hover:from-blue-700 hover:to-blue-800 shadow-md hover:shadow-lg transition-all font-semibold text-sm hover:-translate-y-1"
                >
                  <FiDownload size={18} />
                  Export CSV
                </CSVLink>
              )}
            </div>
          </div>

          {municipalityData.length > 0 ? (
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
              <ResponsiveContainer width="100%" height={450}>
                <BarChart data={municipalityData} margin={{ bottom: 90, left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="municipality"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                  />
                  <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ paddingTop: "10px" }} />
                  <Bar dataKey="total_users" fill="#3b82f6" name="Total Users" radius={[12, 12, 0, 0]} />
                  <Bar dataKey="present" fill="#10b981" name="Present" radius={[12, 12, 0, 0]} />
                  <Bar dataKey="absent" fill="#ef4444" name="Absent" radius={[12, 12, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-96 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-2xl border border-gray-100">
              <FiActivity className="text-6xl mb-3 opacity-20" />
              <p className="text-sm font-medium">No attendance data for selected filters</p>
              <p className="text-xs text-gray-500 mt-1">Try selecting a different date or district</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPark;


